import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import InvoiceTableDataLayer from './InvoiceTableDataLayer';
import { ConfigContext } from '../context/GlobalContext';
import * as actionType from "../context/actions";
import { Languages } from '../Utils/Languages';
import { FreePlanLimitReachedModal } from './FreePlanLimitReached';
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { useLocation } from "react-router-dom";
import AddAuthUserDataInFirebase from '../data/Firebase/AgencyMember/AddAgencyMemberDataInFirebase';
import AgencyMembersTableDataLayer from './AgencyMembersTableDataLayer';
import UpdateAuthUserDataInFirebase from '../data/Firebase/AgencyMember/UpdateAgencyMemberDataInFirebase';
import { InviteMailAPI } from '../MailAPIs/InviteMailAPI';
import { InviteMobileAPI } from '../MailAPIs/InviteMobileAPI';
import { AssignMailAPI } from '../MailAPIs/AssignMailAPI';
import { AssignMobileAPI } from '../MailAPIs/AssignMobileAPI';
import { toast } from 'react-toastify';
import Country from "../Utils/countriesJson.json";
import GetAuthUserDataInFirebase from '../data/Firebase/AgencyMember/GetAgencyMemberDataInFirebase';
import { logEvent } from '../helper/shakebug';

const AgencyUserListLayer = ({ businessData = [], invoiceData = [] }) => {
    // Custom styles for the search input to remove blue focus border
    const focusStyles = `
        .search-input-group .form-control:focus {
            border-color: var(--border-color) !important;
            box-shadow: none !important;
        }
    `;

    const { state, dispatch } = useContext(ConfigContext);
    const navigate = useNavigate();

    const [selectedBusiness, setSelectedBusiness] = useState("null");
    const [isEditMode, setIsEditMode] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [searchedUser, setSearchedUser] = useState(null);
    const [selectedBusinesses, setSelectedBusinesses] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState("google.com");
    const [searchLoading, setSearchLoading] = useState(false);
    const [countryCode, setCountryCode] = useState("+91");
    const [filteredCountries, setFilteredCountries] = useState(Country);
    const [searchCountryText, setSearchCountryText] = useState("");
    const [authuserdata, setAuthUserData] = useState([]);
    const [inviteInfo, setInviteInfo] = useState(null); // { type, value }
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState(false);

    const providerOptions = [
        { value: "google.com", label: "Google", icon: "logos:google-icon" },
        { value: "facebook.com", label: "Facebook", icon: "logos:facebook" },
        { value: "apple.com", label: "Apple", icon: "logos:apple" },
        { value: "phone", label: "Phone", icon: "mdi:phone" },
    ];

    const businessOptions = businessData?.filter(b => !b.isShared).map((b) => ({
        value: b.id,
        label: b.companyName
    }));
    const isDark = localStorage.getItem("theme") === "dark";

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: "var(--neutral-50)",
            borderColor: "var(--border-color)",
            color: "var(--text-secondary-light)",
            boxShadow: "none",
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: "var(--neutral-50)",
            border: "1px solid var(--border-color)",
            zIndex: 9999,
        }),
        singleValue: (base) => ({
            ...base,
            color: "var(--text-secondary-light)",
        }),
        placeholder: (base) => ({
            ...base,
            color: "var(--text-secondary-light)",
        }),
        input: (base) => ({
            ...base,
            color: "var(--text-secondary-light)",
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? "var(--neutral-200)"
                : "transparent",
            color: "var(--text-secondary-light)",
            cursor: "pointer",
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: "var(--neutral-200)",
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: "var(--text-secondary-light)",
            fontWeight: 500,
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: "var(--text-secondary-light)",
            ":hover": {
                backgroundColor: "var(--primary-600, #7c3aed)",
                color: "#fff",
            },
        }),
    };
    const sortedBusinesses = useMemo(() => {
        return [...(state?.BUSINESSES || [])]
            .filter(b => !b.isShared) // ✅ Only own businesses, exclude shared
            .sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));
    }, [state?.BUSINESSES]);

    const resetModalState = () => {
        setSearchValue("");
        setSearchedUser(null);
        setSelectedBusinesses([]);
        setIsEditMode(false);
        setInviteInfo(null);
        setInviteSuccess(false);
        setSelectedProvider("google.com");
    };

    const handleCloseModal = () => {
        setShowAssignModal(false);
        resetModalState();
    };

    // Automatically search when provider changes if search value exists
    useEffect(() => {
        if (searchValue.trim() && !isEditMode && showAssignModal) {
            handleSearchUser();
        }
    }, [selectedProvider]);

    const handleSelection = (businessId) => {
        if (!state?.USERID && businessId !== "null") {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }
        setSelectedBusiness(businessId);
        dispatch({ type: actionType.SELECTEDBUSINESS, payload: businessId });
    };

    const handleAssignUser = async () => {

        if (!searchedUser) return;

        logEvent("member_assigned", searchedUser?.email || searchedUser?.phone || searchedUser?.uid || "");

        const businessIds = selectedBusinesses.map(b => b.value);

        // check if user already assigned
        const existingUser = state?.AGENCYMEMBER?.find(
            u => u.uid === searchedUser.uid
        );

        const selectedBusinessObj = state?.BUSINESSES?.find(b => b.companyId === selectedBusiness);
        const targetUid = selectedBusinessObj?.isShared ? selectedBusinessObj.ownerUid : state?.USERID;

        // Use the providerId from searchedUser, or fallback to selectedProvider, or "password"
        const providerId = searchedUser.providerId || searchedUser.providers?.[0]?.providerId || selectedProvider || "password";

        let response;
        if (existingUser) {

            // UPDATE existing user businesses
            response = await UpdateAuthUserDataInFirebase(
                state,
                dispatch,
                searchedUser.uid,
                businessIds,
                targetUid,
                providerId
            );

        } else {

            // ADD new user
            response = await AddAuthUserDataInFirebase(
                state,
                dispatch,
                searchedUser.uid,
                businessIds,
                searchedUser.name,
                targetUid,
                searchedUser.phone,
                providerId
            );

        }
        setShowAssignModal(false);

        if (response?.success) {
            await GetAuthUserDataInFirebase(state, dispatch, state?.USERID);

            // ✅ Only send notifications if it's a NEW assignment (not an update to existing)
            if (!existingUser) {
                const assignerName = state?.SET_USER?.name || "Assigner";
                const businessNames = selectedBusinesses.map(b => b.label);

                if (searchedUser.email) {
                    await AssignMailAPI(searchedUser.email, businessNames, assignerName);
                }
                if (searchedUser.phone) {
                    await AssignMobileAPI(searchedUser.phone, businessNames, assignerName);
                }
            }
        }

        setSelectedBusinesses([]);
        setSearchedUser(null);
        setSearchValue("");
        setInviteInfo(null);
    };

    const handleSearchUser = async () => {
        const query = searchValue.trim();
        if (!query) return;

        if (selectedProvider === 'phone' && query.includes('@')) {
            toast.error("You don't add email to phone provider");
            return;
        }

        setSearchLoading(true);

        try {
            // Merge country code if phone
            const finalQuery = selectedProvider === 'phone' ? `${countryCode}${query}`.trim() : query;

            let url = `${process.env.REACT_APP_BASE_URL}users/search?query=${encodeURIComponent(finalQuery)}`;
            if (selectedProvider && selectedProvider !== 'phone') {
                url += `&provider=${selectedProvider}`;
            }

            const response = await fetch(url);

            const result = await response.json();

            if (result.status) {

                // ✅ Block self-assignment
                if (result.data.uid === state?.USERID) {
                    toast.error("Please use different credentials. You cannot assign yourself.");
                    setSearchedUser(null);
                    setInviteInfo(null);
                    setSearchLoading(false);
                    return;
                }

                setSearchedUser({
                    userId: result.data.uid,
                    name: result.data.name || result.data.phone || "No Name",
                    uid: result.data.uid,
                    email: result.data.email,
                    phone: result.data.phone,
                    providers: result.data.providers || [],
                    providerId: selectedProvider || result.data.providers?.[0]?.providerId || "password"
                });

                // Pre-populate businesses if user already exists
                const existingUser = state?.AGENCYMEMBER?.find(u => u.uid === result.data.uid);
                if (existingUser && existingUser.company) {
                    setSelectedBusinesses(
                        existingUser.company?.map((c) => ({
                            value: c,
                            label: state?.BUSINESSES?.find(b => b.id === c)?.companyName || "Unknown Business"
                        })) || []
                    );
                } else {
                    const currentBizId = state?.SELECTEDBUSINESS;
                    if (currentBizId && currentBizId !== "null") {
                        const currentBiz = state?.BUSINESSES?.find(b => String(b.companyId) === String(currentBizId));
                        if (currentBiz && !currentBiz.isShared) {
                            setSelectedBusinesses([{
                                value: currentBiz.id,
                                label: currentBiz.companyName
                            }]);
                        } else {
                            setSelectedBusinesses([]);
                        }
                    } else {
                        setSelectedBusinesses([]);
                    }
                }

                setInviteInfo(null);
                setInviteSuccess(false);

            } else {
                const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query);
                const isPhone = /^\+?[1-9]\d{9,14}$/.test(query);

                if (isEmail) {
                    setInviteInfo({ type: 'email', value: query });
                } else if (isPhone) {
                    setInviteInfo({ type: 'phone', value: query });
                } else {
                    toast.error(result.message || "User not found");
                    setInviteInfo(null);
                }
                setSearchedUser(null);
            }
        } catch (error) {
            console.error(error);
            toast.error("Search failed");
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSendInvite = async () => {
        if (!inviteInfo) return;

        setInviteLoading(true);
        const assignerName = state?.SET_USER?.name || "an Assigner";

        try {
            let res;
            if (inviteInfo.type === 'email') {
                res = await InviteMailAPI(inviteInfo.value, assignerName);
            } else if (inviteInfo.type === 'phone') {
                res = await InviteMobileAPI(inviteInfo.value, assignerName);
            }

            if (res) {
                toast.success(`Invitation sent successfully to ${inviteInfo.value}!`);
                logEvent("member_invited", inviteInfo.value || "");
                setInviteSuccess(true);
                // setInviteInfo(null);  // Don't null immediately so we can show the success message
                setSearchValue("");
            } else {
                toast.error("Failed to send invitation.");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred while sending invitation.");
        } finally {
            setInviteLoading(false);
        }
    };


    return (
        <div className="card basic-data-table">

            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">

                {/* ================= BUSINESS DROPDOWN ================= */}
                <div className="dropdown">
                    <button
                        className="btn btn-primary-600 bg-primary-50 border-primary-50 text-primary-600 dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                    >
                        {(state?.SELECTEDBUSINESS == null || state?.SELECTEDBUSINESS === "null")
                            ? Languages[state?.LANGUAGE]?.All_Business
                            : businessData?.find(b => b.companyId === state?.SELECTEDBUSINESS)?.companyName}
                    </button>

                    <ul className="dropdown-menu">

                        {/* ALL BUSINESS OPTION */}
                        <li>
                            <button
                                onClick={() => {
                                    handleSelection("null");
                                }}
                                className="dropdown-item text-secondary-light text-hover-neutral-900 bg-hover-neutral-200"
                            >
                                {Languages[state?.LANGUAGE]?.All_Business}
                            </button>
                        </li>

                        {/* BUSINESS LIST */}
                        {sortedBusinesses.map((business) => {
                            return (
                                <li key={business.companyId}>
                                    <button
                                        onClick={() => handleSelection(business.companyId)}
                                        className={`dropdown-item d-flex align-items-center gap-2
                text-secondary-light bg-hover-neutral-200 text-hover-neutral-900
                ${business.companyId === selectedBusiness
                                                ? "bg-primary-50 fw-semibold selected-business"
                                                : ""
                                            }`}
                                    >
                                        {business.companyName}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* ================= CREATE INVOICE BUTTON ================= */}
                <button
                    onClick={() => {
                        if (!state?.USERID) {
                            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                            return;
                        }
                        setIsEditMode(false);
                        setShowAssignModal(true);
                    }}
                    className="btn btn-success px-18 py-11 d-flex align-items-center"
                >
                    <Icon icon="mdi:account-plus" className="me-2" />
                    Assign User
                </button>

            </div>

            <AgencyMembersTableDataLayer
                selectedBusiness={selectedBusiness}
                membersData={state?.AGENCYMEMBER || []}
                onEditUser={(user) => {
                    if (!state?.USERID) {
                        dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                        return;
                    }

                    setIsEditMode(true);

                    setSearchedUser({
                        uid: user.uid,
                        name: user.name,
                        providerId: user.providerId
                    });

                    if (user.providerId) {
                        setSelectedProvider(user.providerId);
                    }

                    setSelectedBusinesses(
                        user.company?.map((c) => ({
                            value: c,
                            label: state?.BUSINESSES?.find(b => b.id === c)?.companyName
                        })) || []
                    );

                    setShowAssignModal(true);
                }}
            />

            <Modal show={showAssignModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEditMode ? "Edit Agency Member" : "Add Agency Member"}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    {/* SEARCH USER */}
                    {!isEditMode && (
                        <div className="d-flex flex-column gap-3 mb-4">

                            <div>
                                <label className="form-label text-sm text-secondary-light fw-bold mb-12">Select Auth Provider</label>
                                <div className="d-flex gap-2">
                                    {providerOptions.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={`btn flex-grow-1 d-flex flex-column align-items-center justify-content-center py-12 gap-2 border transition-all
                                                ${selectedProvider === opt.value
                                                    ? "fw-bold shadow-sm"
                                                    : "text-secondary-light"}`}
                                            style={{
                                                backgroundColor: selectedProvider === opt.value ? 'var(--primary-50)' : 'var(--neutral-100)',
                                                borderColor: selectedProvider === opt.value ? 'var(--primary-600)' : 'var(--border-color)',
                                                color: selectedProvider === opt.value ? 'var(--primary-600)' : 'var(--text-secondary-light)',
                                            }}
                                            onClick={() => setSelectedProvider(opt.value)}
                                        >
                                            <Icon icon={opt.icon} width={24} style={{ color: selectedProvider === opt.value ? 'var(--primary-600)' : 'inherit' }} />
                                            <span className="text-xs text-uppercase tracking-wider">{opt.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex-grow-1 mt-2">
                                <style>{focusStyles}</style>
                                <label className="form-label text-sm text-secondary-light fw-bold mb-2">Search User</label>
                                <div className="input-group search-input-group">
                                    <span className="input-group-text text-secondary-light d-flex align-items-center justify-content-center" style={{ backgroundColor: "var(--neutral-50)", borderColor: "var(--border-color)" }}>
                                        <Icon icon="lucide:user" />
                                    </span>
                                    {selectedProvider === 'phone' && (
                                        <div className="dropdown">
                                            <button
                                                className="btn border text-secondary-light d-flex align-items-center h-100 px-3"
                                                type="button"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                                style={{
                                                    minWidth: "100px",
                                                    borderTopRightRadius: 0,
                                                    borderBottomRightRadius: 0,
                                                    borderBottomLeftRadius: 0,
                                                    borderTopLeftRadius: 0,
                                                    borderColor: "var(--border-color)",
                                                    borderLeft: 0,
                                                    backgroundColor: "var(--neutral-50)",
                                                    color: "var(--text-secondary-light)"
                                                }}
                                            >
                                                {(() => {
                                                    const selected = Country.find(c => c.dialCode == countryCode);
                                                    return (
                                                        <div className="d-flex align-items-center gap-1" style={{ lineHeight: 1 }}>
                                                            <img
                                                                src={selected?.image}
                                                                alt="flag"
                                                                style={{ width: "20px", height: "14px", objectFit: "cover" }}
                                                            />
                                                            <span className="text-xs fw-bold" style={{ lineHeight: 1 }}>{countryCode}</span>
                                                        </div>
                                                    );
                                                })()}
                                            </button>
                                            <ul className="dropdown-menu shadow-md border-0 p-2 hide-scrollbar" style={{ maxHeight: "250px", overflowY: "auto", backgroundColor: "var(--neutral-100)", borderColor: "var(--border-color)" }}>
                                                <li className="px-2 pb-2 sticky-top" style={{ backgroundColor: "var(--neutral-100)" }}>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Search code"
                                                        value={searchCountryText}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setSearchCountryText(val);
                                                            const filtered = Country.filter(c =>
                                                                c.name.toLowerCase().includes(val.toLowerCase()) ||
                                                                c.dialCode.includes(val)
                                                            );
                                                            setFilteredCountries(filtered);
                                                        }}
                                                        style={{
                                                            backgroundColor: "var(--neutral-50)",
                                                            borderColor: "var(--border-color)",
                                                            color: "var(--text-primary-light)"
                                                        }}
                                                    />
                                                </li>
                                                {filteredCountries.map((c) => (
                                                    <li key={c.id}>
                                                        <button
                                                            className={`dropdown-item d-flex align-items-center gap-2 py-8 rounded ${c.dialCode === countryCode ? "bg-primary-50 text-primary-600" : "text-secondary-light"}`}
                                                            onClick={() => {
                                                                setCountryCode(c.dialCode);
                                                                setSearchCountryText("");
                                                                setFilteredCountries(Country);
                                                            }}
                                                            style={{
                                                                backgroundColor: c.dialCode === countryCode ? 'var(--primary-50)' : 'transparent',
                                                                color: c.dialCode === countryCode ? 'var(--primary-600)' : 'var(--text-secondary-light)'
                                                            }}
                                                        >
                                                            <img src={c.image} alt={c.name} style={{ width: "20px", height: "14px", objectFit: "cover" }} />
                                                            <span className="text-xs">{c.dialCode}</span>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <input
                                        type="text"
                                        className="form-control shadow-none"
                                        style={{
                                            backgroundColor: "var(--neutral-50)",
                                            borderColor: "var(--border-color)",
                                            color: "var(--text-primary-light)",
                                            borderLeft: 0,
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                        }}
                                        placeholder={selectedProvider === 'phone' ? "Enter phone number" : "Enter email address"}
                                        value={searchValue}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (selectedProvider === 'phone') {
                                                // Only allow numbers for phone
                                                const numericVal = val.replace(/\D/g, '');
                                                setSearchValue(numericVal);
                                            } else {
                                                setSearchValue(val);
                                            }
                                        }}
                                    />
                                    <button
                                        className="btn btn-primary px-24 d-flex align-items-center gap-2"
                                        onClick={handleSearchUser}
                                        disabled={searchLoading}
                                    >
                                        {searchLoading ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                            <>
                                                <Icon icon="lucide:search" width={18} />
                                                Search
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="text-xs text-secondary-light mt-2 opacity-75">
                                    We will search for the user linked with the selected provider.
                                </p>
                            </div>

                        </div>
                    )}

                    {/* INVITE SECTION */}
                    {inviteInfo && !inviteSuccess && (
                        <div className="p-24 bg-primary-50 border border-primary-100 radius-12 mb-24">
                            <div className="d-flex align-items-start gap-3">
                                <div className="w-48-px h-48-px bg-primary-600 text-white rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                                    <Icon icon="solar:user-plus-bold" width={24} />
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className="text-md fw-bold text-primary-600 mb-1">User Not Registered</h6>
                                    <p className="text-sm text-secondary-light mb-16">
                                        No BillNama account was found for <span className="fw-bold text-neutral-800">{inviteInfo.value}</span>. Would you like to invite them to join your business?
                                    </p>
                                    <button
                                        className="btn btn-primary px-24 py-10 d-flex align-items-center gap-2"
                                        onClick={handleSendInvite}
                                        disabled={inviteLoading}
                                    >
                                        {inviteLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                Sending Invitation...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon="solar:letter-opened-bold" width={18} />
                                                Send Invite Link
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* INVITE SUCCESS SECTION */}
                    {inviteSuccess && inviteInfo && (
                        <div className="p-24 bg-success-50 border border-success-100 radius-12 mb-24 anim-fade-in">
                            <div className="d-flex align-items-start gap-3">
                                <div className="w-48-px h-48-px bg-success-600 text-white rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                                    <Icon icon="solar:check-read-bold" width={24} />
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className="text-md fw-bold text-success-600 mb-1">Success! Invitation Sent</h6>
                                    <p className="text-sm text-secondary-light mb-0">
                                        Once <span className="fw-bold text-neutral-800">{inviteInfo.value}</span> joins BillNama, simply search for them again to complete the business assignment.
                                    </p>
                                    <button
                                        className="btn btn-outline-success btn-sm mt-16"
                                        onClick={() => {
                                            setInviteInfo(null);
                                            setInviteSuccess(false);
                                        }}
                                    >
                                        Got it, thanks!
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* USER RESULT */}
                    {searchedUser && (
                        <div className="mb-24 anim-fade-in">
                            <label className="form-label text-sm text-secondary-light fw-bold mb-12">Search Result</label>
                            <div
                                className="p-20 rounded-12 border d-flex align-items-center gap-3"
                                style={{
                                    backgroundColor: "var(--neutral-50)",
                                    borderColor: "var(--border-color)",
                                }}
                            >
                                {/* User Avatar Icon */}
                                <div className="w-56-px h-56-px bg-primary-100 text-primary-600 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 border border-primary-200">
                                    <Icon icon="solar:user-circle-bold" width={32} />
                                </div>

                                <div className="flex-grow-1 overflow-hidden">
                                    <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                                        <h6 className="text-md fw-bold text-neutral-800 mb-0 truncate">{searchedUser.name}</h6>

                                        {/* Provider Badge */}
                                        {searchedUser.providerId && (
                                            <span className="badge bg-primary-50 text-primary-600 px-12 py-6 rounded-pill d-flex align-items-center gap-1 border border-primary-200">
                                                <Icon
                                                    icon={providerOptions.find(p => p.value === searchedUser.providerId)?.icon || "mdi:key"}
                                                    width={14}
                                                />
                                                <span className="text-xxs text-uppercase fw-bold">{searchedUser.providerId.split(".")[0]}</span>
                                            </span>
                                        )}
                                    </div>

                                    <div className="d-flex flex-column gap-1">
                                        {searchedUser.email && (
                                            <div className="d-flex align-items-center gap-2 text-xs text-secondary-light">
                                                <Icon icon="lucide:mail" width={14} />
                                                <span className="truncate">{searchedUser.email}</span>
                                            </div>
                                        )}
                                        {searchedUser.phone && (
                                            <div className="d-flex align-items-center gap-2 text-xs text-secondary-light">
                                                <Icon icon="lucide:phone" width={14} />
                                                <span>{searchedUser.phone}</span>
                                            </div>
                                        )}
                                        <div className="d-flex align-items-center gap-2 text-xs text-secondary-light opacity-75">
                                            <Icon icon="lucide:fingerprint" width={14} />
                                            <span className="truncate">UID: {searchedUser.uid}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* BUSINESS MULTI SELECT */}
                    <label className="mb-2" style={{ color: "var(--text-secondary-light)" }}>Assign Businesses</label>

                    <Select
                        options={businessOptions}
                        isMulti
                        value={selectedBusinesses}
                        onChange={setSelectedBusinesses}
                        placeholder="Select Businesses"
                        styles={customSelectStyles}
                        classNamePrefix="theme-select"
                    />

                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleCloseModal}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleAssignUser}
                        disabled={!searchedUser || selectedBusinesses.length === 0}
                    >
                        Assign
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default AgencyUserListLayer;
